import React, { useEffect, useState } from 'react';
import { dialogConfigs } from '../Utils/PMHelper';
import { TIndustry } from '../Types/domain.types';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Textarea from 'Components/DesignSystem/Textarea/Textarea';

interface CreateDomainDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    isLoading?: boolean;
    editData?: TIndustry | null;
    mode?: 'create' | 'edit';
    id?: string;
}

/** The two fields, in `dialogConfigs.createDomain` order. */
type DomainForm = {
    domainName: string;
    description: string;
};

const EMPTY_FORM: DomainForm = { domainName: '', description: '' };

/**
 * Create / Edit Domain on the design-system `Dialog` (§ 119), the same swap § 118 made
 * for the party dialog. `Components/CustomDialog` still serves Create Channel and Add
 * Environment untouched.
 *
 * What it fixes here: `CustomDialog`'s testids are all `id ? `${id}-save-btn` :
 * undefined` and this caller never passed an `id`, so **Save, Cancel and Close had no
 * `data-testid`**; its `<label>`s carry no `htmlFor` and its inputs no `id`, so nothing
 * was associated and `getByLabelText` could not work; and the overlay was a plain
 * `<div>` with no `role="dialog"`, no focus trap and no Escape.
 *
 * **Behaviour held identical.** `description` is optional and `domainName` required,
 * exactly as the descriptor declared. `onSave` still receives the raw
 * `{ domainName, description }` — `ManageDomain` maps it to the API's
 * `{ name, description, active }` for create and `{ active, description, id, name,
 * sams_id }` for edit, and neither shape moves. The "Creating..." busy label is
 * `CustomDialog`'s and stays, wrong in edit mode, for the reason given in § 118.
 *
 * Note there is **no required-field validation**, here or before: Save is always
 * enabled and an empty name reaches `POST party/{id}/industry`. Unchanged on purpose.
 */
export const CreateDomainDialog = ({ 
    isOpen, 
    onClose, 
    onSave, 
    isLoading = false,
    editData = null,
    mode = 'create',
    id,
}: CreateDomainDialogProps) => {
    const [formData, setFormData] = useState<DomainForm>(EMPTY_FORM);

    const baseId = id || `domain-${mode}-dialog`;

    /* Seed on open, per § 118: seeding covers both modes on the render that opens, so
       a reset in a close branch would be unreachable. Keyed on `isOpen` alone. */
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (mode === 'edit' && editData) {
            setFormData({
                domainName: editData.name,
                description: editData.description || '',
            });
        } else {
            setFormData(EMPTY_FORM);
        }
    }, [isOpen]);

    const setField = (name: keyof DomainForm, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /* `CustomDialog` refused to close while loading, on the X and on Cancel. `Dialog`
       routes Escape to the same callback, so the guard lives in the handler. */
    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            title={mode === 'edit' ? 'Edit Domain' : dialogConfigs.createDomain.title}
            size="md"
            data-testid={baseId}
            footer={
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <Button
                        variant="secondary"
                        id={`${baseId}-cancel-btn`}
                        data-testid={`${baseId}-cancel-btn`}
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        id={`${baseId}-save-btn`}
                        data-testid={`${baseId}-save-btn`}
                        onClick={() => onSave(formData)}
                        /* § 209 — loading implies the disabled gate this carried,
                           plus the spinner, aria-busy and the wait cursor. */
                        loading={isLoading}
                        className="tw-min-w-[92px]"
                    >
                        {isLoading ? 'Creating...' : (mode === 'edit' ? 'Update' : 'Create')}
                    </Button>
                </div>
            }
        >
            <div className="tw-space-y-3">
                <TextField
                    label="Domain Name"
                    required
                    placeholder="Enter"
                    value={formData.domainName}
                    onChange={(value) => setField('domainName', value)}
                    disabled={isLoading}
                    id={`${baseId}-name`}
                    data-testid={`${baseId}-name`}
                />
                {/* `rows={3}` was `CustomDialog`'s textarea default. */}
                <Textarea
                    label="Description"
                    placeholder="Enter"
                    rows={3}
                    value={formData.description}
                    onChange={(value) => setField('description', value)}
                    disabled={isLoading}
                    id={`${baseId}-description`}
                    data-testid={`${baseId}-description`}
                />
            </div>
        </Dialog>
    );
};
